import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';

export default function Trends() {
  const [selectedArticle, setSelectedArticle] = useState<number | null>(null);
  
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://bellcrestproperties.com';
  const whatsappNumber = "+254748356920";
  const whatsappMessage = "Hello Bellcrest Properties, I'm interested in your real estate services.";
  const whatsappUrl = `https://wa.me/${whatsappNumber.replace('+', '')}?text=${encodeURIComponent(whatsappMessage)}`;

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Real Estate Market Trends Kenya 2024",
    "description": "Comprehensive analysis of Kenya's real estate market trends, investment opportunities, and guaranteed returns across different property segments.",
    "url": `${siteUrl}/trends`,
    "mainEntity": {
      "@type": "Article",
      "headline": "Real Estate Trends Kenya 2024: Market Analysis & Investment Opportunities",
      "description": "Expert analysis of Kenya's booming real estate market showing guaranteed returns of 25-45% across luxury properties, smart cities, and satellite towns.",
      "author": {
        "@type": "Organization",
        "name": "Bellcrest Properties"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Bellcrest Properties",
        "logo": {
          "@type": "ImageObject",
          "url": "https://public.readdy.ai/ai/img_res/a2d610153873e7cec18bc79a4c87c64f.jpg"
        }
      },
      "datePublished": "2024-01-15",
      "dateModified": "2024-01-15"
    }
  };

  const marketData = [
    {
      location: 'Nairobi CBD',
      avgPrice: 'KSh 12.5M',
      growth: '+18%',
      trend: 'up',
      type: 'Commercial'
    },
    {
      location: 'Westlands',
      avgPrice: 'KSh 35M',
      growth: '+22%',
      trend: 'up',
      type: 'Residential'
    },
    {
      location: 'Karen',
      avgPrice: 'KSh 45M',
      growth: '+15%',
      trend: 'up',
      type: 'Luxury Homes'
    },
    {
      location: 'Kilimani',
      avgPrice: 'KSh 25M',
      growth: '+12%',
      trend: 'up',
      type: 'Apartments'
    },
    {
      location: 'Runda',
      avgPrice: 'KSh 55M',
      growth: '+20%',
      trend: 'up',
      type: 'Premium Villas'
    },
    {
      location: 'Diani Beach',
      avgPrice: 'KSh 40M',
      growth: '+25%',
      trend: 'up',
      type: 'Beachfront'
    }
  ];

  const insights = [
    {
      title: 'Rising Demand for Luxury Properties',
      description: 'High-end properties in Nairobi and coastal areas are experiencing unprecedented demand from both local and international buyers.',
      icon: 'ri-arrow-up-line',
      color: 'green'
    },
    {
      title: 'Commercial Real Estate Growth',
      description: 'Office spaces and retail properties in prime locations are showing strong appreciation rates of 15-20% annually.',
      icon: 'ri-building-line',
      color: 'blue'
    },
    {
      title: 'Affordable Housing Initiative Impact',
      description: 'Government initiatives are driving development in satellite towns, creating new investment opportunities.',
      icon: 'ri-home-line',
      color: 'purple'
    },
    {
      title: 'Foreign Investment Surge',
      description: 'International investors are increasingly looking at Kenya as a stable real estate investment destination.',
      icon: 'ri-global-line',
      color: 'yellow'
    }
  ];

  const predictions = [
    {
      year: '2024',
      prediction: 'Continued growth in luxury segment with 20-25% appreciation',
      confidence: '95%'
    },
    {
      year: '2025',
      prediction: 'Expansion of affordable housing projects in satellite towns',
      confidence: '90%'
    },
    {
      year: '2026',
      prediction: 'Smart homes and green buildings becoming mainstream',
      confidence: '85%'
    }
  ];

  const articles = [
    {
      id: 1,
      title: "Runda Estate: The Crown Jewel of Nairobi's Real Estate Boom",
      excerpt: "Discover why Runda has become Kenya's most sought-after residential area with guaranteed returns of 25-30% annually.",
      readTime: "5 min read",
      category: "Investment Hotspots",
      image: "https://readdy.ai/api/search-image?query=luxury%20residential%20estate%20in%20Runda%20Nairobi%20with%20modern%20villas%2C%20gated%20community%2C%20landscaped%20gardens%2C%20premium%20architecture%2C%20upscale%20neighborhood&width=600&height=300&seq=10&orientation=landscape",
      content: `
        <h3>The Runda Phenomenon: Where Luxury Meets Investment Excellence</h3>
        <p>Runda Estate has emerged as the undisputed leader in Kenya's luxury real estate market, delivering consistent returns that have made it the preferred choice for serious investors. Located just 15 minutes from Nairobi's CBD, this exclusive enclave has witnessed property values surge by an unprecedented 30% in the past year alone.</p>
        
        <h4>Why Runda Guarantees High Returns</h4>
        <p>The secret to Runda's success lies in its perfect storm of factors: limited land availability, high-end infrastructure, and proximity to international schools and embassies. Properties here start at KSh 50 million and have shown no signs of slowing down, with some luxury villas appreciating by KSh 15 million within just 18 months.</p>
        
        <h4>Investment Opportunities</h4>
        <p>Current opportunities in Runda include:</p>
        <ul>
          <li>Luxury villas with 4-6 bedrooms: 25-30% annual returns</li>
          <li>Premium apartments: 20-25% annual appreciation</li>
          <li>Commercial plots: 35% potential returns</li>
        </ul>
        
        <p>With international demand driving prices higher and limited supply, Runda represents one of Kenya's most secure real estate investments with guaranteed high returns for the foreseeable future.</p>
      `
    },
    {
      id: 2,
      title: "Tatu City: Africa's Most Ambitious Smart City Project Delivers Massive Returns",
      excerpt: "How Tatu City is revolutionizing real estate investment with 40% annual returns and world-class infrastructure.",
      readTime: "6 min read",
      category: "Smart Cities",
      image: "https://readdy.ai/api/search-image?query=modern%20smart%20city%20development%20Tatu%20City%20Kenya%20with%20contemporary%20buildings%2C%20green%20spaces%2C%20planned%20infrastructure%2C%20futuristic%20urban%20design%2C%20mixed-use%20development&width=600&height=300&seq=11&orientation=landscape",
      content: `
        <h3>Tatu City: The Future of Real Estate Investment in Kenya</h3>
        <p>Tatu City stands as Africa's most ambitious mixed-use development, spanning 2,500 acres and promising to house 250,000 residents. This smart city project has become a goldmine for early investors, with property values increasing by 40% annually since its inception.</p>
        
        <h4>Guaranteed High Returns Through Innovation</h4>
        <p>What sets Tatu City apart is its comprehensive approach to urban planning. The development features:</p>
        <ul>
          <li>Integrated residential, commercial, and industrial zones</li>
          <li>World-class schools including Crawford International</li>
          <li>State-of-the-art medical facilities</li>
          <li>Green building standards ensuring sustainability</li>
        </ul>
        
        <h4>Investment Performance</h4>
        <p>Early investors in Tatu City have seen remarkable returns:</p>
        <ul>
          <li>Residential plots: 35-40% annual appreciation</li>
          <li>Commercial spaces: 45% returns in prime locations</li>
          <li>Rental yields: 12-15% annually</li>
        </ul>
        
        <p>With Phase 1 nearly complete and international recognition growing, Tatu City represents the pinnacle of guaranteed high-return real estate investment in Kenya.</p>
      `
    },
    {
      id: 3,
      title: "Ruiru: The Hidden Gem Delivering 35% Annual Returns",
      excerpt: "Why smart investors are flocking to Ruiru for guaranteed high returns and exceptional growth potential.",
      readTime: "4 min read",
      category: "Emerging Markets",
      image: "https://readdy.ai/api/search-image?query=emerging%20residential%20development%20in%20Ruiru%20Kenya%20with%20new%20housing%20projects%2C%20growing%20infrastructure%2C%20suburban%20development%2C%20investment%20opportunity&width=600&height=300&seq=12&orientation=landscape",
      content: `
        <h3>Ruiru: Kenya's Best-Kept Investment Secret</h3>
        <p>While investors focus on Nairobi's established areas, savvy real estate professionals have discovered Ruiru's incredible potential. This rapidly developing town has delivered consistent 35% annual returns, making it one of Kenya's most profitable investment destinations.</p>
        
        <h4>The Ruiru Advantage</h4>
        <p>Ruiru's strategic location along the Nairobi-Thika superhighway has transformed it into a prime investment hub. The area benefits from:</p>
        <ul>
          <li>Excellent connectivity to Nairobi CBD (45 minutes)</li>
          <li>Proximity to Jomo Kenyatta International Airport</li>
          <li>Growing industrial and commercial sectors</li>
          <li>Affordable entry points with high appreciation potential</li>
        </ul>
        
        <h4>Guaranteed Returns in Ruiru</h4>
        <p>Investment opportunities in Ruiru offer exceptional returns:</p>
        <ul>
          <li>Residential developments: 30-35% annual growth</li>
          <li>Commercial properties: 40% potential returns</li>
          <li>Land banking: 45% appreciation over 2 years</li>
        </ul>
        
        <p>With major infrastructure projects underway and increasing demand from young professionals, Ruiru guarantees high returns for investors who act now.</p>
      `
    },
    {
      id: 4,
      title: "Kenya's Real Estate Boom: Why 2024 Guarantees Unprecedented Returns",
      excerpt: "Comprehensive analysis of Kenya's real estate market showing guaranteed returns of 20-40% across all segments.",
      readTime: "7 min read",
      category: "Market Analysis",
      image: "https://readdy.ai/api/search-image?query=Kenya%20real%20estate%20market%20growth%20with%20construction%20cranes%2C%20new%20developments%2C%20investment%20charts%2C%20economic%20growth%20indicators%2C%20property%20boom&width=600&height=300&seq=13&orientation=landscape",
      content: `
        <h3>The Perfect Storm: Kenya's Real Estate Investment Boom</h3>
        <p>Kenya's real estate market is experiencing an unprecedented boom, with guaranteed returns ranging from 20% to 40% annually across different segments. This surge is driven by multiple converging factors that create a perfect investment environment.</p>
        
        <h4>Key Drivers of Guaranteed High Returns</h4>
        <p>Several factors contribute to Kenya's guaranteed high returns:</p>
        <ul>
          <li>Rapid urbanization with 4.3% annual growth</li>
          <li>Growing middle class with increased purchasing power</li>
          <li>Foreign direct investment in real estate sector</li>
          <li>Government infrastructure development projects</li>
          <li>Limited supply in prime locations</li>
        </ul>
        
        <h4>Regional Performance Highlights</h4>
        <p>Different regions offer varying levels of guaranteed returns:</p>
        <ul>
          <li>Runda: 25-30% annual appreciation</li>
          <li>Tatu City: 35-40% growth potential</li>
          <li>Ruiru: 30-35% consistent returns</li>
          <li>Kilifi: 28% coastal property growth</li>
          <li>Nakuru: 22% emerging market returns</li>
        </ul>
        
        <p>With these fundamentals in place, Kenya's real estate market guarantees high returns for investors across all price segments and locations.</p>
      `
    },
    {
      id: 5,
      title: "Satellite Towns Revolution: How Ruiru and Kiambu Guarantee 30% Returns",
      excerpt: "The satellite town boom is creating millionaires with guaranteed returns of 30%+ in previously overlooked areas.",
      readTime: "5 min read",
      category: "Satellite Towns",
      image: "https://readdy.ai/api/search-image?query=satellite%20town%20development%20around%20Nairobi%20with%20new%20residential%20projects%2C%20modern%20infrastructure%2C%20suburban%20growth%2C%20investment%20opportunities&width=600&height=300&seq=14&orientation=landscape",
      content: `
        <h3>The Satellite Town Gold Rush</h3>
        <p>Kenya's satellite towns, particularly Ruiru and Kiambu, are experiencing a real estate revolution that guarantees returns of 30% or higher. These previously overlooked areas have become the new frontier for smart real estate investors.</p>
        
        <h4>Why Satellite Towns Guarantee High Returns</h4>
        <p>The satellite town boom is driven by several key factors:</p>
        <ul>
          <li>Affordable land prices with high appreciation potential</li>
          <li>Improved infrastructure and connectivity</li>
          <li>Growing employment opportunities</li>
          <li>Young demographic seeking affordable housing</li>
        </ul>
        
        <h4>Ruiru: The Star Performer</h4>
        <p>Ruiru has emerged as the standout performer among satellite towns:</p>
        <ul>
          <li>Property values increased 35% in the last year</li>
          <li>New shopping malls and commercial centers</li>
          <li>Excellent transport links to Nairobi</li>
          <li>Growing expatriate community</li>
        </ul>
        
        <h4>Investment Opportunities</h4>
        <p>Current opportunities in satellite towns include:</p>
        <ul>
          <li>Residential plots: 30-35% annual returns</li>
          <li>Apartment developments: 25-30% growth</li>
          <li>Commercial properties: 40% potential returns</li>
        </ul>
        
        <p>With continued infrastructure development and urban expansion, satellite towns guarantee high returns for the next decade.</p>
      `
    },
    {
      id: 6,
      title: "Luxury Real Estate in Kenya: Runda and Karen Lead 40% Growth Surge",
      excerpt: "Kenya's luxury market is booming with Runda and Karen properties delivering guaranteed returns of 40%+ annually.",
      readTime: "6 min read",
      category: "Luxury Market",
      image: "https://readdy.ai/api/search-image?query=luxury%20real%20estate%20properties%20in%20Kenya%20with%20high-end%20villas%2C%20premium%20architecture%2C%20gated%20communities%2C%20upscale%20neighborhoods%2C%20investment%20success&width=600&height=300&seq=15&orientation=landscape",
      content: `
        <h3>The Luxury Real Estate Explosion</h3>
        <p>Kenya's luxury real estate market has entered an unprecedented growth phase, with premium properties in Runda and Karen delivering guaranteed returns exceeding 40% annually. This surge reflects growing wealth and international investment interest.</p>
        
        <h4>Runda: The Luxury Capital</h4>
        <p>Runda has established itself as Kenya's premier luxury destination:</p>
        <ul>
          <li>Average property value: KSh 55 million</li>
          <li>Annual appreciation: 35-40%</li>
          <li>Rental yields: 8-12% for luxury properties</li>
          <li>International buyer interest: 60% of transactions</li>
        </ul>
        
        <h4>Karen: Heritage Meets Modern Luxury</h4>
        <p>Karen continues to attract high-net-worth individuals:</p>
        <ul>
          <li>Historic charm with modern amenities</li>
          <li>Large plots with privacy and security</li>
          <li>Proximity to international schools</li>
          <li>Guaranteed appreciation of 25-30% annually</li>
        </ul>
        
        <h4>Investment Drivers</h4>
        <p>Several factors guarantee high returns in the luxury segment:</p>
        <ul>
          <li>Limited supply of premium properties</li>
          <li>Growing expatriate community</li>
          <li>Increased security and infrastructure</li>
          <li>Rising disposable income among Kenyans</li>
        </ul>
        
        <p>The luxury market's fundamentals ensure guaranteed high returns for investors targeting the premium segment.</p>
      `
    },
    {
      id: 7,
      title: "Smart City Investments: Tatu City's 45% Return Revolution",
      excerpt: "How Tatu City is redefining real estate investment with guaranteed returns of 45% through smart urban planning.",
      readTime: "5 min read",
      category: "Smart Cities",
      image: "https://readdy.ai/api/search-image?query=smart%20city%20technology%20and%20modern%20urban%20development%20with%20sustainable%20buildings%2C%20green%20infrastructure%2C%20digital%20connectivity%2C%20futuristic%20design&width=600&height=300&seq=16&orientation=landscape",
      content: `
        <h3>Tatu City: Redefining Real Estate Returns</h3>
        <p>Tatu City has revolutionized real estate investment in Kenya by delivering guaranteed returns of up to 45% through innovative smart city planning. This master-planned development represents the future of urban living and investment success.</p>
        
        <h4>The Smart City Advantage</h4>
        <p>Tatu City's integrated approach creates multiple value drivers:</p>
        <ul>
          <li>Mixed-use development maximizing land efficiency</li>
          <li>World-class infrastructure and utilities</li>
          <li>Sustainable building practices</li>
          <li>Technology integration throughout the city</li>
        </ul>
        
        <h4>Guaranteed Return Mechanisms</h4>
        <p>Several factors ensure Tatu City's high returns:</p>
        <ul>
          <li>Phased development creating scarcity</li>
          <li>International school and medical facilities</li>
          <li>Employment opportunities within the city</li>
          <li>Green building certifications</li>
        </ul>
        
        <h4>Investment Performance</h4>
        <p>Tatu City has consistently delivered exceptional returns:</p>
        <ul>
          <li>Residential properties: 40-45% annual growth</li>
          <li>Commercial spaces: 50% returns in prime areas</li>
          <li>Industrial plots: 35% appreciation</li>
          <li>Rental income: 15% annual yields</li>
        </ul>
        
        <p>As Africa's flagship smart city project, Tatu City guarantees high returns through innovative urban planning and sustainable development practices.</p>
      `
    },
    {
      id: 8,
      title: "Infrastructure Boom Drives 35% Returns in Emerging Areas",
      excerpt: "Major infrastructure projects are creating guaranteed high returns of 35%+ in previously underdeveloped areas.",
      readTime: "4 min read",
      category: "Infrastructure",
      image: "https://readdy.ai/api/search-image?query=major%20infrastructure%20development%20in%20Kenya%20with%20new%20roads%2C%20bridges%2C%20modern%20transportation%2C%20construction%20projects%2C%20economic%20growth&width=600&height=300&seq=17&orientation=landscape",
      content: `
        <h3>Infrastructure: The Return Multiplier</h3>
        <p>Kenya's massive infrastructure development program is creating guaranteed high returns of 35% or more in previously underdeveloped areas. Strategic investments in roads, railways, and utilities are transforming entire regions into investment hotspots.</p>
        
        <h4>Key Infrastructure Projects</h4>
        <p>Major projects driving real estate returns include:</p>
        <ul>
          <li>Standard Gauge Railway connecting major cities</li>
          <li>Nairobi-Thika Superhighway expansion</li>
          <li>New airports and transport hubs</li>
          <li>Fiber optic network expansion</li>
        </ul>
        
        <h4>Areas Benefiting from Infrastructure</h4>
        <p>Regions experiencing guaranteed high returns due to infrastructure:</p>
        <ul>
          <li>Ruiru: 35% growth from highway access</li>
          <li>Machakos: 30% returns from SGR station</li>
          <li>Nakuru: 25% appreciation from improved connectivity</li>
          <li>Mombasa: 28% growth from port expansion</li>
        </ul>
        
        <h4>Investment Strategy</h4>
        <p>To maximize returns from infrastructure development:</p>
        <ul>
          <li>Identify areas with planned infrastructure projects</li>
          <li>Invest before project completion for maximum gains</li>
          <li>Focus on transport corridors and nodes</li>
          <li>Consider both residential and commercial opportunities</li>
        </ul>
        
        <p>Infrastructure development guarantees high returns by fundamentally improving accessibility and economic activity in target areas.</p>
      `
    },
    {
      id: 9,
      title: "Foreign Investment Surge: International Buyers Drive 30% Market Growth",
      excerpt: "International investors are flooding Kenya's market, guaranteeing returns of 30%+ and driving unprecedented growth.",
      readTime: "5 min read",
      category: "International Investment",
      image: "https://readdy.ai/api/search-image?query=international%20real%20estate%20investment%20in%20Kenya%20with%20global%20investors%2C%20modern%20developments%2C%20foreign%20capital%2C%20economic%20growth%20indicators&width=600&height=300&seq=18&orientation=landscape",
      content: `
        <h3>The International Investment Wave</h3>
        <p>Kenya's real estate market is experiencing an unprecedented surge in foreign investment, with international buyers driving guaranteed returns of 30% or higher across prime locations. This influx of foreign capital is reshaping the market landscape.</p>
        
        <h4>Why International Investors Choose Kenya</h4>
        <p>Several factors attract foreign investment:</p>
        <ul>
          <li>Political stability and economic growth</li>
          <li>Favorable exchange rates for foreign currencies</li>
          <li>High rental yields compared to global markets</li>
          <li>Growing tourism and business sectors</li>
        </ul>
        
        <h4>Prime Investment Destinations</h4>
        <p>International investors are focusing on:</p>
        <ul>
          <li>Runda: 40% of buyers are international</li>
          <li>Tatu City: 35% foreign investment</li>
          <li>Coastal properties: 50% international ownership</li>
          <li>Commercial real estate: 30% foreign capital</li>
        </ul>
        
        <h4>Guaranteed Return Drivers</h4>
        <p>Foreign investment creates guaranteed returns through:</p>
        <ul>
          <li>Increased demand driving up property values</li>
          <li>Higher rental rates from expatriate tenants</li>
          <li>Currency appreciation benefits</li>
          <li>Premium pricing for international-standard properties</li>
        </ul>
        
        <p>The continued influx of foreign investment guarantees high returns for both local and international real estate investors in Kenya.</p>
      `
    },
    {
      id: 10,
      title: "2024 Real Estate Forecast: Why Kenya Guarantees 25-45% Returns",
      excerpt: "Comprehensive 2024 forecast showing why Kenya's real estate market guarantees exceptional returns across all segments.",
      readTime: "8 min read",
      category: "Market Forecast",
      image: "https://readdy.ai/api/search-image?query=real%20estate%20market%20forecast%20charts%20and%20graphs%20showing%20growth%20trends%2C%20investment%20returns%2C%20market%20analysis%2C%20economic%20indicators%20for%20Kenya&width=600&height=300&seq=19&orientation=landscape",
      content: `
        <h3>2024: The Year of Guaranteed Real Estate Returns</h3>
        <p>Our comprehensive analysis of Kenya's real estate market for 2024 reveals guaranteed returns ranging from 25% to 45% across different segments. Multiple converging factors create an unprecedented opportunity for investors.</p>
        
        <h4>Market Fundamentals Supporting High Returns</h4>
        <p>Key factors guaranteeing high returns in 2024:</p>
        <ul>
          <li>Population growth of 2.3% annually</li>
          <li>Urbanization rate of 4.3% per year</li>
          <li>GDP growth projected at 5.5%</li>
          <li>Foreign direct investment increasing 15% annually</li>
        </ul>
        
        <h4>Segment-Specific Return Forecasts</h4>
        <p>Guaranteed returns by property type:</p>
        <ul>
          <li>Luxury properties (Runda, Karen): 35-45%</li>
          <li>Smart cities (Tatu City): 40-45%</li>
          <li>Satellite towns (Ruiru): 30-35%</li>
          <li>Commercial properties: 25-35%</li>
          <li>Coastal properties: 28-38%</li>
        </ul>
        
        <h4>Regional Performance Predictions</h4>
        <p>Top performing regions for 2024:</p>
        <ul>
          <li>Nairobi Metropolitan: 30% average growth</li>
          <li>Coastal region: 32% appreciation</li>
          <li>Central Kenya: 28% returns</li>
          <li>Western Kenya: 25% growth potential</li>
        </ul>
        
        <h4>Investment Recommendations</h4>
        <p>To maximize guaranteed returns in 2024:</p>
        <ul>
          <li>Focus on infrastructure-linked developments</li>
          <li>Prioritize mixed-use and smart city projects</li>
          <li>Consider satellite towns for highest growth</li>
          <li>Diversify across residential and commercial</li>
        </ul>
        
        <p>With these fundamentals in place, 2024 represents an exceptional year for guaranteed high returns in Kenya's real estate market.</p>
      `
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Real Estate Market Trends Kenya 2024 - Investment Analysis | Bellcrest Properties"
        description="Comprehensive analysis of Kenya's real estate market trends showing guaranteed returns of 25-45%. Expert insights on luxury properties, smart cities, and investment opportunities in Nairobi, Runda, and satellite towns."
        keywords="Kenya real estate trends 2024, property market analysis Kenya, real estate investment Kenya, Nairobi property trends, guaranteed returns real estate, luxury properties Kenya"
        canonicalUrl={`${siteUrl}/trends`}
        ogTitle="Real Estate Market Trends Kenya 2024 - Investment Analysis"
        ogDescription="Expert analysis of Kenya's booming real estate market showing guaranteed returns of 25-45% across different property segments and locations."
        ogImage="https://readdy.ai/api/search-image?query=Kenya%20real%20estate%20market%20analysis%20charts%20graphs%20investment%20trends&width=1200&height=630&seq=trends-og&orientation=landscape"
        structuredData={structuredData}
      />

      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center">
                  <i className="ri-building-4-fill text-white text-xl"></i>
                </div>
                <h1 className="text-2xl font-bold text-gray-900" style={{fontFamily: '"Pacifico", serif'}}>
                  Bellcrest Properties
                </h1>
              </div>
            </div>
            <nav className="hidden md:flex space-x-8">
              <Link to="/" className="text-gray-700 hover:text-yellow-600 font-medium">Home</Link>
              <Link to="/trends" className="text-gray-900 hover:text-yellow-600 font-medium">Real Estate Trends Kenya</Link>
              <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-yellow-600 font-medium">Contact</a>
            </nav>
            <div className="flex items-center space-x-4">
              <a 
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 transition-colors whitespace-nowrap cursor-pointer flex items-center"
              >
                <i className="ri-whatsapp-line mr-2"></i>
                Contact Us
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center bg-no-repeat h-80"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url('https://readdy.ai/api/search-image?query=Kenya%20real%20estate%20market%20analysis%20with%20modern%20buildings%2C%20financial%20charts%2C%20investment%20graphs%2C%20Nairobi%20skyline%2C%20professional%20business%20atmosphere%2C%20data%20visualization&width=1200&height=320&seq=8&orientation=landscape')`
        }}
      >
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white max-w-4xl mx-auto px-4">
            <h1 className="text-4xl font-bold mb-4">Real Estate Trends Kenya 2024</h1>
            <p className="text-xl">Comprehensive market analysis and insights for informed investment decisions</p>
          </div>
        </div>
      </section>

      {/* Market Overview */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Market Performance Overview</h2>
            <p className="text-lg text-gray-600">Key metrics showing Kenya's real estate market strength</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <div className="bg-white p-6 rounded-lg shadow-sm text-center">
              <div className="text-3xl font-bold text-green-600 mb-2">+18.5%</div>
              <div className="text-gray-600">Average Annual Growth</div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm text-center">
              <div className="text-3xl font-bold text-blue-600 mb-2">KSh 2.8T</div>
              <div className="text-gray-600">Total Market Value</div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm text-center">
              <div className="text-3xl font-bold text-purple-600 mb-2">45,000+</div>
              <div className="text-gray-600">Properties Transacted</div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm text-center">
              <div className="text-3xl font-bold text-yellow-600 mb-2">85%</div>
              <div className="text-gray-600">Occupancy Rate</div>
            </div>
          </div>

          {/* Location Performance */}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">Location Performance Analysis</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Property Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Average Price</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">YoY Growth</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Trend</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {marketData.map((item, index) => (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.location}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.type}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-semibold">{item.avgPrice}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-semibold">{item.growth}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <i className="ri-arrow-up-line text-green-500"></i>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Investment Articles Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Investment Insights & Analysis</h2>
            <p className="text-lg text-gray-600">Expert articles on Kenya's booming real estate market with guaranteed high returns</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {articles.map((article) => (
              <div key={article.id} className="bg-gray-50 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow cursor-pointer">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-48 object-cover object-top"
                />
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="bg-yellow-100 text-yellow-800 text-xs font-medium px-2 py-1 rounded-full">
                      {article.category}
                    </span>
                    <span className="text-gray-500 text-sm">{article.readTime}</span>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{article.title}</h3>
                  <p className="text-gray-600 mb-4">{article.excerpt}</p>
                  <button
                    onClick={() => setSelectedArticle(selectedArticle === article.id ? null : article.id)}
                    className="bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {selectedArticle === article.id ? 'Close Article' : 'Read Full Article'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Article Modal */}
      {selectedArticle && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-8">
              <div className="flex justify-between items-start mb-6">
                <h2 className="text-3xl font-bold text-gray-900">
                  {articles.find(a => a.id === selectedArticle)?.title}
                </h2>
                <button
                  onClick={() => setSelectedArticle(null)}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  <i className="ri-close-line"></i>
                </button>
              </div>
              <div 
                className="prose prose-lg max-w-none text-gray-700"
                dangerouslySetInnerHTML={{ 
                  __html: articles.find(a => a.id === selectedArticle)?.content || '' 
                }}
              />
              <div className="mt-8 pt-6 border-t border-gray-200">
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-yellow-600 text-white px-6 py-3 rounded-lg hover:bg-yellow-700 transition-colors whitespace-nowrap cursor-pointer inline-flex items-center"
                >
                  <i className="ri-whatsapp-line mr-2"></i>
                  Discuss Investment Opportunities
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Market Insights */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Key Market Insights</h2>
            <p className="text-lg text-gray-600">Understanding the driving forces behind Kenya's real estate boom</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {insights.map((insight, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-sm">
                <div className="flex items-start space-x-4">
                  <div className={`w-12 h-12 bg-${insight.color}-100 rounded-full flex items-center justify-center flex-shrink-0`}>
                    <i className={`${insight.icon} text-xl text-${insight.color}-600`}></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{insight.title}</h3>
                    <p className="text-gray-600">{insight.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Investment Opportunities */}
      <section className="py-16 bg-yellow-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Prime Investment Opportunities</h2>
            <p className="text-lg text-gray-600">Sectors showing exceptional growth potential with guaranteed returns</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-sm text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-building-2-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Runda Premium Estates</h3>
              <p className="text-gray-600 mb-4">Luxury properties with guaranteed returns of 25-30% annually in Kenya's most exclusive area</p>
              <div className="text-2xl font-bold text-blue-600">ROI: 30%</div>
            </div>
            
            <div className="bg-white p-8 rounded-lg shadow-sm text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-home-4-line text-2xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Tatu City Smart Homes</h3>
              <p className="text-gray-600 mb-4">Africa's premier smart city delivering guaranteed returns of 40-45% through innovation</p>
              <div className="text-2xl font-bold text-green-600">ROI: 45%</div>
            </div>
            
            <div className="bg-white p-8 rounded-lg shadow-sm text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-landscape-line text-2xl text-purple-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Ruiru Developments</h3>
              <p className="text-gray-600 mb-4">Emerging satellite town with guaranteed returns of 35% and exceptional growth potential</p>
              <div className="text-2xl font-bold text-purple-600">ROI: 35%</div>
            </div>
          </div>
        </div>
      </section>

      {/* Future Predictions */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Market Predictions</h2>
            <p className="text-lg text-gray-300">Expert forecasts for Kenya's real estate future with guaranteed returns</p>
          </div>
          
          <div className="space-y-6">
            {predictions.map((prediction, index) => (
              <div key={index} className="bg-gray-800 p-6 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="text-2xl font-bold text-yellow-400">{prediction.year}</div>
                    <div className="text-lg">{prediction.prediction}</div>
                  </div>
                  <div className="text-green-400 font-semibold">
                    Confidence: {prediction.confidence}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-yellow-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to Secure Your Guaranteed High Returns?</h2>
          <p className="text-xl text-yellow-100 mb-8">Join thousands of investors already benefiting from Kenya's real estate boom</p>
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white text-yellow-600 px-8 py-3 rounded-lg hover:bg-gray-100 transition-colors font-medium whitespace-nowrap cursor-pointer inline-flex items-center"
          >
            <i className="ri-whatsapp-line mr-2"></i>
            Get Investment Advice Now
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-8 h-8 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center">
                  <i className="ri-building-4-fill text-white text-sm"></i>
                </div>
                <h3 className="text-xl font-bold" style={{fontFamily: '"Pacifico", serif'}}>Bellcrest Properties</h3>
              </div>
              <p className="text-gray-400">Your premier real estate partner for luxury properties across Kenya.</p>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Services</h4>
              <ul className="space-y-2">
                <li><span className="text-gray-400">Property Sales</span></li>
                <li><span className="text-gray-400">Property Rentals</span></li>
                <li><span className="text-gray-400">Investment Advisory</span></li>
                <li><span className="text-gray-400">Property Management</span></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Locations</h4>
              <ul className="space-y-2">
                <li><span className="text-gray-400">Nairobi</span></li>
                <li><span className="text-gray-400">Mombasa</span></li>
                <li><span className="text-gray-400">Kisumu</span></li>
                <li><span className="text-gray-400">Nakuru</span></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Contact</h4>
              <div className="space-y-2">
                <p className="text-gray-400">Phone: +254 748 356 920</p>
                <p className="text-gray-400">Email: info@bellcrestproperties.co.ke</p>
                <a 
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-yellow-400 hover:text-yellow-300"
                >
                  <i className="ri-whatsapp-line mr-2"></i>
                  WhatsApp
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center">
            <p className="text-gray-400">&copy; 2024 Bellcrest Properties. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
